import React from 'react';
import './TermsAndConditions.css';
import { Helmet } from 'react-helmet';

const TermsAndConditions = () => {

  return (
<>
<Helmet>
    <title>Terms & Conditions | SkillzVerse</title>

    <meta
        name="description"
        content="Read the SkillzVerse Terms & Conditions to understand user responsibilities, account policies, course access, payments, intellectual property, and platform usage guidelines."
    />

    <meta
        name="keywords"
        content="Terms and Conditions,Terms of Use,User Agreement,Website Terms,Platform Terms and Conditions,Online Learning Terms"
    />

    <link
        rel="canonical"
        href="https://skillzverse.com/terms-conditions"
    />
</Helmet>
    <div className="terms-container">
      <h1>SkillzVerse Terms and Conditions</h1>
      {/* <p><strong>Effective Date:</strong> 1st Jan 2025</p> */}
      <p>Welcome to SkillzVerse—a platform where learning meets limitless possibilities! These Terms and Conditions ("Terms") govern your use of our site, app, and services ("Platform"). By using SkillzVerse, you're agreeing to these Terms. If you're not on board, please don't use our services. Let's get started.</p>

      <section className="terms-section">
        <h2>1. Who We Are, and Who You Are</h2>
        <p>At SkillzVerse, we’re all about empowering you to grow, learn, and achieve your potential. When you sign up, you join a community of learners and creators from around the world. You’re a "User" (that’s you!), and SkillzVerse is the "Platform" (that’s us). By agreeing to these Terms, you enter into a contract with SkillzVerse, a [jurisdiction] company. If you're under 18, please don't use SkillzVerse unless you have permission from a parent or legal guardian.</p>
      </section>

      <section className="terms-section">
        <h2>2. Your Account – Let’s Keep It Secure</h2>
        <p>To dive into SkillzVerse, you need an account. Setting up an account is easy, but with great access comes great responsibility.
          Here’s what we need from you:
          <ul>
            <li><strong>Accuracy:</strong> Make sure the info you provide is true, accurate, and up-to-date. We don’t want to learn anything about you that’s not real.</li>
            <li><strong>Security:</strong> Keep your account secure. Use a strong password and don’t share your login info with others.</li>
            <li><strong>Responsibility:</strong> You’re responsible for everything that happens under your account, so treat it like your own private space.</li>
          </ul>
          If you think someone else is using your account without permission, let us know ASAP so we can fix it.</p>
      </section>

      <section className="terms-section">
        <h2>3. The Content – What You Get, What You Can Do</h2>
        <p>SkillzVerse offers content to help you grow, including courses, workshops, videos, and more. Some content is made by SkillzVerse, and some is from independent creators. You get access to the content you purchase or have permission to use. However, you can’t redistribute or modify the content unless specifically allowed.</p>
      </section>

      <section className="terms-section">
        <h2>4. Payments & Pricing</h2>
        <p>We want you to learn and grow, and sometimes that involves paying for premium content. Here’s what you need to know:
          <ul>
            <li><strong>Pricing:</strong> Prices for courses or services are clearly listed before you purchase. We may adjust pricing, but we’ll notify you if it affects anything you’ve already purchased.</li>
            <li><strong>Payments:</strong> SkillzVerse uses secure payment processors. We accept multiple forms of payment, but please note that you’ll be responsible for any taxes that apply.</li>
            <li><strong>Refunds:</strong> We want you to love your experience with SkillzVerse. If you're unhappy with a course or service, check out our Refund Policy for more details on how to request a refund. Some Content, however, may have different refund terms depending on the creator’s policies.</li>
          </ul>
        </p>
      </section>

      <section className="terms-section">
        <h2>5. Your Content – Share It, But Play Nice</h2>
        <p>We love when you contribute to the community. You’re responsible for the content you share, and we encourage you to respect others. When you post content, you confirm that it’s yours and that you have the right to share it. We may use your content to promote SkillzVerse and improve the learning experience.</p>
      </section>

      <section className="terms-section">
        <h2>6. No Drama, Please – What You Can’t Do</h2>
        <p>At SkillzVerse, we’re here to learn, grow, and support each other. So, there are some basic rules for using the Platform:
          You agree not to:
          <ul>
            <li><strong>Disrupt the platform:</strong> Don’t try to hack, overload, or interfere with SkillzVerse’s technology.</li>
            <li><strong>Misuse your account:</strong> Don’t steal, share, or sell access to your account. You’re the only one who should be using it (unless we say otherwise).</li>
            <li><strong>Break the law:</strong> No illegal activity. This includes plagiarism, fraud, or anything else that violates the law.</li>
          </ul>
          If you break these rules, we might suspend or terminate your account.</p>
      </section>

      <section className="terms-section">
        <h2>7. Your Data – We Keep It Safe, You Keep Control</h2>
        <p>Your privacy is important to us. By using SkillzVerse, you trust us with your information, and we take that seriously.
          We collect data to improve your experience, but we also want you to know exactly how we handle it. So, please read our Privacy Policy carefully.
          You have control over your data, including how you update or delete your information. We’ll keep your personal data secure, but remember, no system is completely foolproof.</p>
      </section>

      <section className="terms-section">
        <h2>8. We’re Not Perfect, But We Try</h2>
        <p>We work hard to provide quality content and a smooth experience, but sometimes things don’t go as planned. Here’s what you need to know:
          <ul>
            <li><strong>No Guarantees:</strong> We make no promises about the outcome of your learning experience, and we don’t guarantee specific results from the courses.</li>
            <li><strong>Availability:</strong> SkillzVerse may be temporarily unavailable due to maintenance or other reasons. We’ll do our best to minimize disruptions, but we can’t promise we’ll always be online 24/7.</li>
          </ul>
        </p>
      </section>

      <section className="terms-section">
        <h2>9. Limitation of Liability</h2>
        <p>SkillzVerse is here to support you, but we can’t be held liable for any indirect, incidental, or consequential damages related to your use of the Platform. This includes things like loss of data, revenue, or business opportunities.
          If something goes wrong and we’re liable, the maximum amount we’ll pay you is the amount you’ve spent on the Platform in the last 12 months. Simple as that.</p>
      </section>

      <section className="terms-section">
        <h2>10. Changes and Updates</h2>
        <p>We’re always improving, and we may update or change SkillzVerse from time to time. If we make major changes to how the Platform works or how we operate, we’ll let you know.
          If you don’t agree with the updated Terms, you can always stop using the Platform, and we’ll issue a refund for any courses you’ve purchased (if applicable).</p>
      </section>

      <section className="terms-section">
        <h2>11. Dispute Resolution – Let’s Keep It Friendly</h2>
        <p>If we ever have a disagreement, we will attempt to resolve it through friendly conversation. If that doesn’t work, we will resolve the issue using [mediation/arbitration] (depending on the jurisdiction), so we can avoid any costly court battles.</p>
      </section>

      <section className="terms-section">
        <h2>12. Legal Stuff – Everything Else You Need to Know</h2>
        <ul>
          <li><strong>Force Majeure:</strong> We’re not liable if we can’t fulfill our obligations due to circumstances beyond our control (such as natural disasters, government restrictions, or other force majeure events).</li>
          <li><strong>Severability:</strong> If any part of these Terms is unenforceable, the remaining provisions will continue to apply.</li>
          <li><strong>Transfer:</strong> We may transfer our rights and obligations to others, but you cannot transfer yours without our approval.</li>
        </ul>
      </section>

      <section className="terms-section">
        <h2>13. Why These Terms Matter</h2>
        <p>We want SkillzVerse to be a place where everyone can learn, grow, and achieve their goals. These Terms help ensure that everything runs smoothly and that everyone—learners and creators alike—has a great experience.
          Thanks for being part of SkillzVerse. Let's make learning limitless!
        </p>
      </section>

      <section className="terms-section">
        <h2>14. Get In Touch</h2>
        <p>If you have any questions?  please <a href="/support" className="contact-link">reach out to us on our contact page</a>.</p>
      </section>

            <section className="terms-section">
        <h2>15. AI Interview</h2>
        <p>In the event that the exam or interview session is closed or interrupted during participation, we shall not be held responsible for any disruptions caused by connectivity issues or technical problems on the candidate's end.</p></section>

    </div>
    </>
  );
};

export default TermsAndConditions;
